import type { FC } from 'react';
import React, { useState, useEffect, useRef } from 'react';
import { Button, Layout } from 'antd';
import examStyle from './index.less';
import Timer from './components/Timer';
import VideoContainer from './components/VideoContainer';
import type { SubmitModalRef } from './components/SubmitModal';
import SubmitModal from './components/SubmitModal';
import { WaterMark } from '@ant-design/pro-layout';
import "github-markdown-css/github-markdown.css";
import { examination } from '@/api/examinationApi';
import { useLocation, history } from "umi";
import RadioList from './components/RadioList';
import CodeList from './components/CodeList';
import { banCheating, windowSwitch } from "./utils/ban";
import config from "@/utils/config";
const { Header, Content } = Layout;
import { loader } from "@monaco-editor/react";

loader.config({ paths: { vs: "https://webcdn.233leyuan.com/common/monaco/1.0.0/vs" } });

type examineeRadioProblem = API.radioProblem & { reply: string }
type examineeCodeProblem = API.codeProblem & { reply: string; language: number }

const radioAnswer = JSON.parse(sessionStorage.getItem('examineeRadioProblem') || '[]');
const codeAnswer = JSON.parse(sessionStorage.getItem('examineeCodeProblem') || '[]');
const questionInfoStorage = JSON.parse(sessionStorage.getItem('questionInfoStorage') || '{}');

/** 题目布局 */
const ExanminationLayout: FC = () => {
  const [questionType, setQuestionType] = useState('radio');
  const submitModalRef = useRef<SubmitModalRef>();
  const { query } = useLocation() as unknown as Location & { query: Record<string, string> };
  const [radioProblems, setRadioProblems] = useState<examineeRadioProblem[]>(radioAnswer);
  const [codeProblems, setCodeProblems] = useState<examineeCodeProblem[]>(codeAnswer);
  const [questionInfo, setQuestionInfo] = useState<Omit<API.examinationContent, 'problems'>>(questionInfoStorage);
  const [windosSwitchTime, setWindosSwitchTime] = useState(0);
  const [isSubmit, setIsSubmit] = useState(false);

  useEffect(() => {
    window.Pandora.send('zijin_testee_page_view', {
      page: 'question',
      onlyid: query.exam_id,
    });
    /** 获取数据 */
    async function getdata() {
      const resSubmitStatus = await examination.submitStatus({ examId: query.exam_id });
      if (resSubmitStatus.data.submitted) {
        history.replace({
          pathname: '/result_exam',
          query: {
            status: '3',
            exam_id: query.exam_id,
          },
        });
        return;
      }
      const [resGetContentRadio, resGetContentCode] = await Promise.all([
        examination.getContentChoice({
          examId: query.exam_id,
        }),
        examination.getContentCode({
          examId: query.exam_id,
        })
      ]);
      setQuestionInfo(resGetContentRadio.data);
      window.sessionStorage.setItem('questionInfoStorage', JSON.stringify(resGetContentRadio.data));
      setRadioProblems((resGetContentRadio.data.problems).map(item => ({
        ...item,
        reply: ''
      })));
      if(resGetContentRadio.data.problems.length === 0) {
        setQuestionType('code');
      }
      /** 编程题相关 */
      setCodeProblems(resGetContentCode.data.problems.map(item => ({
        ...item,
        reply: item.templateFunctions?.[0].languageTemplate,
        language: item.templateFunctions?.[0].languageId
      })));
    }
    if(radioAnswer.length === 0 && codeAnswer.length === 0) {
      getdata();
    } else {
      if(radioAnswer.length === 0) {
        setQuestionType('code');
      }
    }
    if(config.nodeEnv === 'production') {
      banCheating();
      windowSwitch((time) => {
        setWindosSwitchTime(time);
      });
    }
    /** 防止用户不小心关闭页签 */
    window.onbeforeunload = () => {
        return true;
    };

  }, [query]);

  /** 打开提交弹窗 */
  const openSubmitMadal = () => {
    window.Pandora.send('zijin_testee_click_submit', {
      onlyid: query.exam_id,
      data: JSON.stringify({
        examId: questionInfo!.examId!,
        candidateId: questionInfo!.candidateId!,
        answers: [
          ...radioProblems.map(item => ({
            problemId: item.problemId,
            reply: item.reply
          })),
          ...codeProblems.map(item => ({
            problemId: item.problemId,
            reply: item.reply,
            languageId: item.language
          }))
        ],
        cheat: [{
          cheatType: 'toggleScreen',
          cheatTime: parseInt(String(windosSwitchTime / 1000), 10)
        }]
      })
    });
    submitModalRef.current?.open({
      examId: questionInfo!.examId!,
      candidateId: questionInfo!.candidateId!,
      answers: [
        ...radioProblems.map(item => ({
          problemId: item.problemId,
          reply: item.reply
        })),
        ...codeProblems.map(item => ({
          problemId: item.problemId,
          reply: item.reply,
          languageId: item.language
        }))
      ],
      cheat: [{
        cheatType: 'toggleScreen',
        cheatTime: parseInt(String(windosSwitchTime / 1000), 10)
      }]
    });
  };

  /** 考试时间到 */
  const timerFinish = () => {
    submitModalRef.current?.timeUseUp({
      examId: questionInfo!.examId!,
      candidateId: questionInfo!.candidateId!,
      answers: [
        ...radioProblems.map(item => ({
          problemId: item.problemId,
          reply: item.reply
        })),
        ...codeProblems.map(item => ({
          problemId: item.problemId,
          reply: item.reply,
          languageId: item.language
        }))
      ],
      cheat: [{
        cheatType: 'toggleScreen',
        cheatTime: parseInt(String(windosSwitchTime / 1000), 10)
      }]
    });
  };

  /** 考生作答的改变 */
  const changeTitle = (type: 'code' | 'radio', problems: examineeRadioProblem[] | examineeCodeProblem[]) => {
    if(type === 'radio') {
      setRadioProblems(problems as examineeRadioProblem[]);
      sessionStorage.setItem('examineeRadioProblem', JSON.stringify(problems));
    } else {
      setCodeProblems(problems as examineeCodeProblem[]);
      sessionStorage.setItem('examineeCodeProblem', JSON.stringify(problems));
    }
  };
  return (
    <Layout className={examStyle.layout}>
      <Header className={examStyle.header}>
        <div style={{ display: 'flex' }}>
          <div className={examStyle.logo}>
            <div className={examStyle.zijin}>子衿</div>
            <div className={examStyle.version}>v1.0-beta</div>
          </div>
          <div className={examStyle.user}>欢迎 {questionInfo?.candidateName} 参加笔试</div>
          {questionInfo && <Timer deadline={questionInfo.startTime + questionInfo.examTime} className={examStyle.timer} onFinish={timerFinish} />}
        </div>
        <div className={examStyle.buttons}>
          {(questionType === 'code' && radioProblems.length > 0) && <Button type="primary" style={{ marginRight: '10px' }} onClick={() => { 
            window.Pandora.send('zijin_testee_change_title', {
              onlyid: query.exam_id,
              type: 'radio'
            });
            setQuestionType('radio'); 
            }}>选择题</Button>}
          {(questionType === 'radio' && codeProblems.length > 0) && <Button type="primary" style={{ marginRight: '10px' }} onClick={() => { 
            window.Pandora.send('zijin_testee_change_title', {
              onlyid: query.exam_id,
              type: 'code'
            });
            setQuestionType('code'); setIsSubmit(true); 
          }}>编程题</Button>}
          <Button
            type="primary"
            disabled={radioProblems.length > 0 && codeProblems.length > 0 && !isSubmit}
            onClick={openSubmitMadal}
          >
            提交
          </Button>
        </div>
      </Header>
      <Content className={examStyle.content}>
        <WaterMark
          content={questionInfo?.candidateName}
          rotate={-25}
          fontColor="rgba(0,0,0,.15)"
          fontSize={16}
          zIndex={32}
          gapX={100}
          gapY={100}
        >
          {(questionType === 'radio' && radioProblems.length > 0) && <RadioList problems={radioProblems} onChange={(val) => { changeTitle('radio', val); }} />}
          {(codeProblems.length > 0) && <CodeList style={{display: questionType === 'code' ? 'block' : 'none'}} problems={codeProblems} onChange={(val) => { changeTitle('code', val); }} />}
        </WaterMark>
      </Content>
      {/* 摄像头、和屏幕切换。防作弊功能 */}
      {config.nodeEnv === 'production' && <VideoContainer className={examStyle.video} />}
      {/* 提交弹窗 */}
      <SubmitModal ref={submitModalRef} />
    </Layout>
  );
};

export default ExanminationLayout;
